import React, { useCallback } from 'react';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { Link } from '@app/core';
import { ValidationResult, ValidationStrategyResult } from '@app/api';
import classNames from 'classnames';

const useStyles = createUseStyles((theme: Theme) => ({
  line: {
    ...theme.fonts.caption,
    marginTop: theme.marginBase,
  },
  strategy: {
    fontWeight: 'bold',
  },
  skip: {
    color: theme.colors.inkTertiary,
  },
  valid: {
    color: theme.colors.inkPrimary,
  },
  block: {
    color: theme.colors.redAccessible,
  },
  validated: {
    color: theme.colors.greenAccessible,
  },
  detailsButton: {
    ...theme.fonts.caption,
    color: theme.colors.blueAccessible,
  },
  details: {
    ...theme.fonts.caption,
    color: theme.colors.inkPrimary,
    backgroundColor: theme.colors.grayBackground,
    maxWidth: '100%',
    overflowX: 'auto',
  },
  score: {
    fontWeight: 'bold',
    color: theme.colors.blueAccessible,
  },
  goodScore: {
    color: theme.colors.greenAccessible,
  },
  badScore: {
    color: theme.colors.redAccessible,
  },
}));

interface Props {
  className?: string;
  result: ValidationStrategyResult;
}

export const AutomaticValidationResultItem = ({ result, className }: Props) => {
  const classes = useStyles();

  const [detailsOpenened, setDetailsOpened] = React.useState(false);

  const toggleDetails = useCallback(() => {
    setDetailsOpened((prev) => !prev);
  }, [setDetailsOpened]);

  return (
    <li className={classNames(className, classes.line, {})} key={result.id}>
      <b className={classes.strategy}>{result.strategy} : </b>
      {typeof result.score === 'number' && (
        <span
          className={classNames(classes.score, {
            [classes.badScore]: result.score < 30,
            [classes.goodScore]: result.score > 80,
          })}
        >
          {result.score}%{' '}
        </span>
      )}

      <span
        className={classNames({
          [classes.skip]: result.result === ValidationResult.SKIP,
          [classes.valid]: result.result === ValidationResult.VALID,
          [classes.block]: result.result === ValidationResult.BLOCK,
          [classes.validated]:
            result.result === ValidationResult.AUTOMATICALLY_ACCEPT,
        })}
      >
        {result.shortText}
      </span>
      <Link className={classes.detailsButton} onClick={toggleDetails}>
        Details
      </Link>
      {detailsOpenened && (
        <div className={classes.details}>
          <pre>
            <code>{JSON.stringify(result.details, undefined, 4)}</code>
          </pre>
        </div>
      )}
    </li>
  );
};
